import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useInspectorRef } from '../../../../hooks/useInspectorRef'
import {
  Box,
  Typography,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack
} from '@mui/material'
import { FileDownload as DownloadIcon, Schedule as ScheduleIcon } from '@mui/icons-material'

import { useInspector } from '../../../../contexts/InspectorContext'
import { api } from '../../../../services/api'
import { downloadBlob } from '../../../../utils/download'
import { formatCurrency } from '../../../../utils/inspectorFormatters'
import { LightspeedIcon, XeroIcon } from '../../../../components/SystemIcons'
import { poolwerxColors } from '../../../../theme/colors'

interface OutletBalance {
  outlet_id: string
  outlet_name: string
  balance_incl_negative: number
  balance_excl_negative: number
  negative_stock_impact: number
  product_count: number
  negative_stock_count: number
}

interface XeroAccount {
  account_code: string
  account_name: string
  balance: number
}

interface InventorySummary {
  ls_total_incl_negative: number
  ls_total_excl_negative: number
  xero_total: number
  variance_excl: number
  variance_incl: number
  variance_percentage_excl: number
  variance_percentage_incl: number
}

interface InventoryBalanceResponse {
  franchise_partner: {
    fp_id: number
    fp_code: string
    franchise_name: string
    country: string
  }
  sync_dates: {
    lightspeed: string | null
    xero: string | null
  }
  lightspeed_outlets: OutletBalance[]
  xero_accounts: XeroAccount[]
  summary: InventorySummary
}

const InventoryBalanceReview: React.FC = () => {
  const { fpCode: fp_code } = useInspectorRef()
  const { franchiseData } = useInspector()

  const franchiseName = franchiseData?.franchise_name || 'Loading...'
  const pageTitle = `Inventory Balance Review | ${franchiseName} | Inspector Tool`

  const [data, setData] = useState<InventoryBalanceResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exportLoading, setExportLoading] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      if (!fp_code) return

      setLoading(true)
      setError(null)

      try {
        const url = `/inspector/inventory-balance-review/${fp_code}`
        const response = await api.get(url)
        setData(response)
      } catch (err: any) {
        setError(err.message || 'Failed to fetch data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [fp_code])

  const formatSyncDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never'
    const date = new Date(dateStr)
    return date.toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    })
  }

  const getVarianceColor = (percentage: number) => {
    const absPercentage = Math.abs(percentage)
    if (absPercentage < 1) return poolwerxColors.success
    if (absPercentage < 5) return '#ff9800' // warning orange
    return poolwerxColors.error
  }

  const handleExportExcel = async () => {
    if (!data) return

    setExportLoading(true)
    try {
      await downloadBlob(
        `/inspector/inventory-balance-review/export/${fp_code}`,
        `inventory-balance-review-${fp_code}`,
        'xlsx'
      )
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setExportLoading(false)
    }
  }

  const summary = data?.summary

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Typography variant="h4" component="h1">
                Inventory Balance Review
              </Typography>
              <Chip
                label="BETA"
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  height: 24,
                  bgcolor: 'warning.main',
                  color: '#fff',
                }}
              />
            </Stack>
            <Typography variant="body1" color="text.secondary">
              Compare inventory balances between Lightspeed and Xero accounting records.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={exportLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleExportExcel}
            disabled={exportLoading || !data}
          >
            Export Excel
          </Button>
        </Box>

        {/* Sync Date Banner */}
        {data && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ScheduleIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" color="text.secondary">
                Data As Of
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LightspeedIcon size={20} />
                  <Typography variant="body2">
                    <strong>Lightspeed:</strong> {formatSyncDate(data.sync_dates.lightspeed)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <XeroIcon size={20} />
                  <Typography variant="body2">
                    <strong>Xero:</strong> {formatSyncDate(data.sync_dates.xero)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Summary Cards - 4 cards */}
        {summary && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <XeroIcon size={18} />
                  Xero Inventory
                </Typography>
                <Typography variant="h6" color={poolwerxColors.blue} sx={{ fontWeight: 'bold', mt: 1 }}>
                  {formatCurrency(summary.xero_total)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LightspeedIcon size={18} />
                  LS Total (excl. neg)
                </Typography>
                <Typography variant="h6" color={poolwerxColors.teal} sx={{ fontWeight: 'bold', mt: 1 }}>
                  {formatCurrency(summary.ls_total_excl_negative)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LightspeedIcon size={18} />
                  LS Total (incl. neg)
                </Typography>
                <Typography variant="h6" color={poolwerxColors.teal} sx={{ fontWeight: 'bold', mt: 1 }}>
                  {formatCurrency(summary.ls_total_incl_negative)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Variance (vs excl. neg)
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', mt: 1, color: getVarianceColor(summary.variance_percentage_excl) }}
                >
                  {formatCurrency(summary.variance_excl)}
                  <span style={{ fontSize: '0.75em', marginLeft: '8px' }}>
                    ({summary.variance_percentage_excl >= 0 ? '+' : ''}{summary.variance_percentage_excl.toFixed(1)}%)
                  </span>
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Data Tables */}
        {!loading && !error && data && (
          <>
            {/* Lightspeed Outlets Table */}
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Lightspeed Inventory by Outlet
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: poolwerxColors.teal }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Outlet</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Balance (incl. neg)</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Balance (excl. neg)</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Negative Stock Impact</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Products</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Neg. Stock Items</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lightspeed_outlets.map((outlet) => (
                    <TableRow key={outlet.outlet_id} hover>
                      <TableCell>{outlet.outlet_name}</TableCell>
                      <TableCell align="right">{formatCurrency(outlet.balance_incl_negative)}</TableCell>
                      <TableCell align="right">{formatCurrency(outlet.balance_excl_negative)}</TableCell>
                      <TableCell align="right" sx={{ color: outlet.negative_stock_impact < 0 ? poolwerxColors.error : 'inherit' }}>
                        {formatCurrency(outlet.negative_stock_impact)}
                      </TableCell>
                      <TableCell align="right">{outlet.product_count.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {outlet.negative_stock_count > 0 ? (
                          <Chip
                            label={outlet.negative_stock_count}
                            size="small"
                            color="warning"
                          />
                        ) : (
                          <span style={{ color: poolwerxColors.success }}>0</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total Row */}
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(summary?.ls_total_incl_negative || 0)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(summary?.ls_total_excl_negative || 0)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: (summary?.ls_total_incl_negative || 0) - (summary?.ls_total_excl_negative || 0) < 0 ? poolwerxColors.error : 'inherit' }}>
                      {formatCurrency((summary?.ls_total_incl_negative || 0) - (summary?.ls_total_excl_negative || 0))}
                    </TableCell>
                    <TableCell align="right"></TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Xero Accounts Table */}
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Xero Inventory Accounts
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: poolwerxColors.blue }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Account Code</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Account Name</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.xero_accounts.length > 0 ? (
                    <>
                      {data.xero_accounts.map((account) => (
                        <TableRow key={account.account_code} hover>
                          <TableCell>{account.account_code}</TableCell>
                          <TableCell>{account.account_name}</TableCell>
                          <TableCell align="right">{formatCurrency(account.balance)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(summary?.xero_total || 0)}</TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        No Xero inventory accounts found with balance data
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Reconciliation Summary */}
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Reconciliation Summary
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.700' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow hover>
                    <TableCell>Lightspeed (excl. negative)</TableCell>
                    <TableCell align="right">{formatCurrency(summary?.ls_total_excl_negative || 0)}</TableCell>
                    <TableCell>Recommended basis</TableCell>
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Lightspeed (incl. negative)</TableCell>
                    <TableCell align="right">{formatCurrency(summary?.ls_total_incl_negative || 0)}</TableCell>
                    <TableCell>Actual stock value</TableCell>
                  </TableRow>
                  <TableRow hover>
                    <TableCell>Xero Inventory Balance</TableCell>
                    <TableCell align="right">{formatCurrency(summary?.xero_total || 0)}</TableCell>
                    <TableCell>GL balance</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Variance (LS excl - Xero)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: getVarianceColor(summary?.variance_percentage_excl || 0) }}>
                      {formatCurrency(summary?.variance_excl || 0)}
                    </TableCell>
                    <TableCell sx={{ color: getVarianceColor(summary?.variance_percentage_excl || 0) }}>
                      {(summary?.variance_percentage_excl || 0) >= 0 ? '+' : ''}{(summary?.variance_percentage_excl || 0).toFixed(2)}% {(summary?.variance_excl || 0) > 0 ? 'higher' : 'lower'} in LS
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Variance (LS incl - Xero)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: getVarianceColor(summary?.variance_percentage_incl || 0) }}>
                      {formatCurrency(summary?.variance_incl || 0)}
                    </TableCell>
                    <TableCell sx={{ color: getVarianceColor(summary?.variance_percentage_incl || 0) }}>
                      {(summary?.variance_percentage_incl || 0) >= 0 ? '+' : ''}{(summary?.variance_percentage_incl || 0).toFixed(2)}% {(summary?.variance_incl || 0) > 0 ? 'higher' : 'lower'} in LS
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Empty State */}
        {!loading && !error && data && data.lightspeed_outlets.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No inventory data available for this franchise partner.
            </Typography>
          </Box>
        )}
      </Box>
    </>
  )
}

export default InventoryBalanceReview
